import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, MapPinned, RefreshCw, Scissors, Sparkles } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import {
  createOffice,
  officeLabel,
  suggestStarostwo,
  type StarostwoProposal,
  type StarostwoSuggestion,
} from "../../api";
import {
  useMzOrderMutations,
  useMzSuggestContext,
  useSelectableOffices,
  usePortfolioOptions,
  useTaskPolling,
} from "../../hooks";
import { TaskLog } from "../ui";
import { apiErr } from "../../utils";
import {
  MZ_ORDER_STATUS_LABELS,
  type MzCountyOption,
  type MzDeliveryMethod,
  type MzPurpose,
  type WypisApplicant,
} from "../../types";

const PURPOSE_OPTIONS: { value: MzPurpose; label: string }[] = [
  { value: "dowolne", label: "Dla dowolnych potrzeb (CL 2,0)" },
  {
    value: "wlasne_niezwiazane",
    label: "Dla potrzeb własnych niezwiązanych z działalnością gospodarczą (CL 1,0)",
  },
];

const DELIVERY_OPTIONS: { value: MzDeliveryMethod; label: string }[] = [
  { value: "email", label: "Wysyłka na wskazany adres e-mail" },
  { value: "ftp", label: "Udostępnienie na serwerze FTP organu" },
  { value: "usluga_sieciowa", label: "Usługa sieciowa udostępniania" },
  { value: "odbior_osobisty", label: "Odbiór osobisty" },
  { value: "poczta", label: "Wysyłka pod wskazany adres (jak w nagłówku)" },
  { value: "nosnik", label: "Na nośniku dostarczonym przez wnioskodawcę" },
];

/** Wiersz powiatu w modalu: co zaznaczone i do którego starostwa idzie wniosek. */
type CountyPick = { checked: boolean; starostwo: string };

// Jeden wniosek = jeden powiat. Zakres to obrys MDCP projektu przycięty do granicy
// powiatu (+0,5 m zapasu), bo starostwo wydaje mapę wyłącznie ze swojego zasobu;
// projekt przechodzący przez granicę dostaje osobny wniosek do każdego starostwa.
export function CreateMzOrderModal({
  connectionId,
  onClose,
}: {
  /** Preselected ZE project (e.g. opened from the project workspace); optional. */
  connectionId?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const { data: portfolio } = usePortfolioOptions();
  const { data: offices } = useSelectableOffices();
  const { data: users } = useInternalUsers();
  const { create } = useMzOrderMutations();

  const [connection, setConnection] = useState(connectionId ?? "");
  const { data: context } = useMzSuggestContext(connection || undefined);
  const [picks, setPicks] = useState<Record<string, CountyPick>>({});
  const [applicant, setApplicant] = useState<WypisApplicant>("elektim");
  const [contactPerson, setContactPerson] = useState(me?.id ? String(me.id) : "");
  const [scale, setScale] = useState("1:500");
  const [purpose, setPurpose] = useState<MzPurpose>("dowolne");
  const [delivery, setDelivery] = useState<MzDeliveryMethod>("email");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Obrys (zakres) wniosku: projekt ma ich wiele, więc wniosek niesie WSKAZANY obrys,
  // a nie „ten akurat wybrany na mapie w dniu generacji". Domyślnie ten z panelu MDCP.
  const [bufferId, setBufferId] = useState("");
  const buffers = context?.buffers ?? [];
  useEffect(() => {
    setBufferId(context?.buffer ?? "");
  }, [context]);
  const chosenBuffer = buffers.find((b) => b.id === bufferId) ?? null;

  // Powiaty trasy z backendu; projekt bez rozpoznanych działek → jeden wiersz
  // „dominującego" powiatu (zachowanie sprzed podziału na powiaty).
  const countyRows: MzCountyOption[] = useMemo(() => {
    if (context?.counties?.length) return context.counties;
    if (!context) return [];
    return [{
      teryt: "",
      county: context.county,
      voivodeship: context.voivodeship,
      label: context.county || "powiat nierozpoznany",
      parcel_count: 0,
      starostwo_matches: context.starostwo_matches ?? [],
      existing_order: null,
    }];
  }, [context]);
  const multiCounty = countyRows.length > 1;

  // Prefill: powiaty bez żywego zamówienia zaznaczone, adresat z rejestru urzędów.
  useEffect(() => {
    setPicks(Object.fromEntries(countyRows.map((row) => [
      row.teryt,
      { checked: !row.existing_order, starostwo: row.starostwo_matches[0]?.id ?? "" },
    ])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countyRows]);

  const setPick = (teryt: string, patch: Partial<CountyPick>) =>
    setPicks((prev) => ({
      ...prev,
      [teryt]: { checked: false, starostwo: "", ...prev[teryt], ...patch },
    }));

  // AI lookup of the EGiB office — same task as in wypisy (registry → TERYT → web).
  // `lookupFor` trzyma powiat, dla którego szukamy, żeby wynik trafił we właściwy wiersz.
  const [aiNote, setAiNote] = useState("");
  const [lookupFor, setLookupFor] = useState<MzCountyOption | null>(null);
  const [proposal, setProposal] = useState<StarostwoProposal | null>(null);
  const [proposalFor, setProposalFor] = useState<string>("");
  const [lookupTaskId, setLookupTaskId] = useState<string | null>(null);
  const lookupTask = useTaskPolling(lookupTaskId);
  const lookupRunning =
    !!lookupTaskId && !["success", "failure", "cancelled"].includes(lookupTask.data?.status ?? "");

  const applySuggestion = (result: StarostwoSuggestion, teryt: string) => {
    if (result.existing.length > 0) {
      setProposal(null);
      setPick(teryt, { starostwo: result.existing[0].id, checked: true });
      setAiNote(`Znaleziono w rejestrze: ${officeLabel(result.existing[0])}`);
    } else if (result.proposal) {
      setProposal(result.proposal);
      setProposalFor(teryt);
      setAiNote(
        result.proposal.via_web_search
          ? "Propozycja z wyszukiwania w internecie — zweryfikuj przed dodaniem."
          : "Propozycja z wiedzy modelu (bez wyszukiwania) — koniecznie zweryfikuj.",
      );
    } else {
      setAiNote("Nie udało się znaleźć urzędu — dodaj go ręcznie.");
    }
  };

  const findStarostwo = useMutation({
    mutationFn: (row: MzCountyOption) => suggestStarostwo(row.county ?? "", row.voivodeship),
    onSuccess: (result, row) => {
      if (result.task_id) setLookupTaskId(result.task_id);
      else applySuggestion(result, row.teryt);
    },
    onError: () => setAiNote("Wyszukiwanie nie powiodło się — dodaj urząd ręcznie."),
  });

  useEffect(() => {
    if (!lookupTaskId || !lookupTask.data) return;
    if (lookupTask.data.status === "success") {
      applySuggestion(
        (lookupTask.data.result ?? { existing: [], proposal: null }) as StarostwoSuggestion,
        lookupFor?.teryt ?? "",
      );
      setLookupTaskId(null);
    } else if (["failure", "cancelled"].includes(lookupTask.data.status)) {
      setAiNote("Wyszukiwanie nie powiodło się — dodaj urząd ręcznie.");
      setLookupTaskId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupTask.data?.status]);

  const addProposedOffice = useMutation({
    mutationFn: () =>
      createOffice({
        name: proposal!.name,
        kind: "starostwo",
        department: proposal!.department,
        street: proposal!.street,
        building_no: proposal!.building_no,
        postal_code: proposal!.postal_code,
        city: proposal!.city,
        phone: proposal!.phone,
        email: proposal!.email,
        bank_account: proposal!.bank_account,
        notes: [
          proposal!.website ? `WWW: ${proposal!.website}` : "",
          proposal!.bank_account_source ? `Źródło numeru konta: ${proposal!.bank_account_source}` : "",
          proposal!.source ? `Dodano z wyszukiwania AI (źródło: ${proposal!.source})` : "Dodano z wyszukiwania AI",
        ].filter(Boolean).join("\n"),
      }),
    onSuccess: (office) => {
      qc.invalidateQueries({ queryKey: ["pp", "offices"] });
      setPick(proposalFor, { starostwo: office.id, checked: true });
      setProposal(null);
      setAiNote(`Dodano i wybrano: ${office.name}`);
    },
    onError: () => setAiNote("Nie udało się dodać urzędu — uzupełnij brakujące pola adresu."),
  });
  const setProposalField = (k: keyof StarostwoProposal, v: string) =>
    setProposal((p) => (p ? { ...p, [k]: v } : p));

  const projectOptions = useMemo(
    () =>
      (portfolio ?? []).map((row) => ({
        value: row.id,
        label: row.project_name + (row.contract_number ? ` (${row.contract_number})` : ""),
      })),
    [portfolio],
  );
  // Mapę zasadniczą wydaje organ POWIATOWY prowadzący zasób geodezyjny — w miastach
  // na prawach powiatu jest nim prezydent miasta (urząd miasta, wydział geodezji), stąd
  // w rejestrze bywa „Urząd Miasta X" z rodzajem „starostwo". Etykieta pokazuje WYDZIAŁ,
  // bo bez niego taki wpis wygląda jak zwykły urząd gminy i budzi słuszne wątpliwości.
  const starostwoOptions = useMemo(
    () =>
      (offices ?? [])
        .filter((o) => o.kind === "starostwo")
        .map((o) => ({ value: o.id, label: officeLabel(o) })),
    [offices],
  );
  const userOptions = useMemo(
    () => (users ?? []).map((u) => ({ value: String(u.id), label: u.full_name || u.username })),
    [users],
  );

  const selectedRows = countyRows.filter((row) => picks[row.teryt]?.checked);
  const canSubmit =
    !!connection && selectedRows.length > 0 && selectedRows.every((r) => picks[r.teryt]?.starostwo);

  const submit = async () => {
    setError("");
    setSubmitting(true);
    const created: string[] = [];
    try {
      // Jeden POST na powiat — numery i opłaty idą osobno dla każdego starostwa.
      for (const row of selectedRows) {
        const order = await create.mutateAsync({
          connection,
          starostwo: picks[row.teryt].starostwo,
          county: row.county || undefined,
          county_teryt: row.teryt || undefined,
          voivodeship: row.voivodeship || undefined,
          applicant,
          contact_person: contactPerson || undefined,
          scale,
          purpose,
          delivery_method: delivery,
          notes: notes || undefined,
          buffer: bufferId || undefined,
        });
        created.push(order.id);
      }
      navigate(created.length === 1 ? `/power-projects/mz/${created[0]}` : "/power-projects/zamawianie?tab=mz");
    } catch (e: unknown) {
      setError(
        (created.length ? `Utworzono ${created.length} z ${selectedRows.length} zamówień. ` : "") +
          apiErr(e, "Nie udało się utworzyć zamówienia."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Zamów mapę zasadniczą (wektor .dxf)" size="lg">
      <div className="space-y-4">
        <label className="block">
          <span className="section-label">Projekt ZE *</span>
          <SearchableSelect
            options={projectOptions}
            value={connection}
            placeholder="Wybierz projekt…"
            onChange={(e) => { setConnection(e.target.value); setPicks({}); setAiNote(""); }}
          />
        </label>

        {connection && context && (
          <div
            className={`flex items-start gap-2 border px-3 py-2 text-xs ${
              context.has_outline
                ? "border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
                : "border-amber-400 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200"
            }`}
          >
            <MapPinned size={14} className="mt-0.5 shrink-0" />
            <div className="space-y-2 w-full">
              {context.has_outline ? (
                <div>
                  Zakres wniosku = <b>obrys MDCP</b> projektu
                  {chosenBuffer ? <> („{chosenBuffer.display_name}”, bufor {chosenBuffer.buffer_width_m} m)</>
                    : <> (bufor {context.buffer_width_m} m)</>}
                  , edytowalny w panelu MDCP. Załącznik DXF z obrysem i wpisanym układem{" "}
                  <b>EPSG:{context.srid}</b> wygeneruje się razem z wnioskiem.
                </div>
              ) : (
                <div>
                  Projekt nie ma jeszcze obrysu MDCP — narysuj trasę lub obrys w panelu MDCP,
                  inaczej generacja wniosku będzie zablokowana.
                </div>
              )}
              {/* Wybór obrysu pokazujemy dopiero, gdy jest z czego wybierać — przy jednym
                  obrysie lista rozwijana z jedną pozycją tylko zaśmieca modal. */}
              {buffers.length > 1 && (
                <label className="block">
                  <span className="section-label">Obrys (zakres wniosku)</span>
                  <select
                    aria-label="Obrys wniosku MZ"
                    className="input-base w-full py-1 text-xs"
                    value={bufferId}
                    onChange={(e) => setBufferId(e.target.value)}
                  >
                    {buffers.map((b) => (
                      <option key={b.id} value={b.id}>
                        {[
                          b.display_name,
                          b.area_m2 != null ? `${(b.area_m2 / 10000).toFixed(2)} ha` : "brak obrysu",
                          b.is_active ? "wybrany na mapie" : null,
                        ].filter(Boolean).join(" · ")}
                      </option>
                    ))}
                  </select>
                  <span className="mt-1 block text-[11px] text-[var(--color-text-muted)]">
                    Zapisuje się na wniosku — późniejsze przełączenie obrysu na mapie projektu
                    nie zmieni zakresu tego wniosku.
                  </span>
                </label>
              )}
            </div>
          </div>
        )}

        {connection && multiCounty && (
          <div className="flex items-start gap-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs">
            <Scissors size={14} className="mt-0.5 shrink-0" />
            <div>
              Projekt leży w <b>{countyRows.length} powiatach</b>. Każde starostwo wydaje mapę
              tylko ze swojego zasobu, więc powstanie <b>osobny wniosek na powiat</b>, a obrys
              w załączniku zostanie przycięty do granicy powiatu z zapasem{" "}
              <b>{String(context?.clip_margin_m ?? 0.5).replace(".", ",")} m</b> (żeby na styku
              powiatów nie było dziury). Granice pobieramy z GUGiK przy generacji dokumentów.
            </div>
          </div>
        )}

        {connection && countyRows.length > 0 && (
          <div className="space-y-2">
            <span className="section-label">
              {multiCounty
                ? "Powiaty i adresaci (jeden wniosek na powiat) *"
                : "Adresat — organ prowadzący zasób geodezyjny *"}
            </span>
            <p className="text-[11px] text-[var(--color-text-muted)] mb-1">
              Mapę zasadniczą wydaje STAROSTWO POWIATOWE, a w miastach na prawach powiatu
              (np. Rybnik, Gdańsk) — prezydent miasta przez wydział geodezji urzędu miasta.
              Lista pokazuje wyłącznie organy tego rodzaju, wraz z wydziałem.
            </p>
            {countyRows.map((row) => {
              const pick = picks[row.teryt] ?? { checked: false, starostwo: "" };
              const busy =
                (findStarostwo.isPending || lookupRunning) && lookupFor?.teryt === row.teryt;
              return (
                <div key={row.teryt || "_"} className="border border-[var(--color-border)] px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Checkbox także dla JEDNEGO powiatu, gdy ma już zamówienie: wiersz
                        startuje odznaczony (ostrzeżenie niżej), więc bez tego pola nie
                        dało się świadomie złożyć kolejnego wniosku — przycisk „Utwórz”
                        zostawał wyszarzony bez żadnego komunikatu. */}
                    {(multiCounty || row.existing_order) && (
                      <input
                        type="checkbox"
                        checked={pick.checked}
                        onChange={(e) => setPick(row.teryt, { checked: e.target.checked })}
                      />
                    )}
                    <div className="min-w-0 text-sm">
                      <b>{row.label}</b>
                      {row.teryt && (
                        <span className="text-[11px] text-[var(--color-text-muted)] ml-1 font-mono">
                          {row.teryt}
                        </span>
                      )}
                      {row.parcel_count > 0 && (
                        <span className="text-[11px] text-[var(--color-text-muted)] ml-2">
                          {row.parcel_count} dz. trasy
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 basis-[16rem]">
                      <SearchableSelect
                        options={starostwoOptions}
                        value={pick.starostwo}
                        placeholder="Wybierz organ…"
                        onChange={(e) => setPick(row.teryt, { starostwo: e.target.value })}
                      />
                    </div>
                    <button
                      type="button"
                      className="p-1.5 border border-[var(--color-border-strong)] hover:bg-[var(--color-bg-elevated)]"
                      title="Odśwież listę urzędów"
                      onClick={() => qc.invalidateQueries({ queryKey: ["pp", "offices"] })}
                    >
                      <RefreshCw size={13} />
                    </button>
                    <button
                      type="button"
                      className="p-1.5 border border-[var(--color-border-strong)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
                      title="Znajdź urząd EGiB (AI: rejestr → internet)"
                      disabled={!row.county || findStarostwo.isPending || lookupRunning}
                      onClick={() => {
                        setAiNote("");
                        setProposal(null);
                        setLookupFor(row);
                        findStarostwo.mutate(row);
                      }}
                    >
                      <Sparkles size={13} className={busy ? "animate-pulse" : ""} />
                    </button>
                  </div>
                  {row.existing_order && (
                    <div className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">
                      Ten powiat ma już zamówienie {row.existing_order.number} (
                      {MZ_ORDER_STATUS_LABELS[row.existing_order.status]}) — zaznacz tylko,
                      jeśli świadomie składasz kolejny wniosek.
                    </div>
                  )}
                  {busy && (
                    <div className="text-[11px] text-[var(--color-text-muted)] mt-1 space-y-0.5">
                      <div className="flex items-center gap-1">
                        <Sparkles size={11} className="animate-pulse" />
                        Szukam urzędu EGiB dla powiatu {row.county}…
                      </div>
                      <TaskLog log={lookupTask.data?.log ?? ""} />
                    </div>
                  )}
                </div>
              );
            })}
            {aiNote && !findStarostwo.isPending && !lookupRunning && (
              <div className="text-[11px] text-[var(--color-text-secondary)]">{aiNote}</div>
            )}
          </div>
        )}

        <label className="block">
          <span className="section-label">Osoba do kontaktów</span>
          <SearchableSelect
            options={userOptions}
            value={contactPerson}
            placeholder="Wybierz osobę…"
            onChange={(e) => setContactPerson(e.target.value)}
          />
        </label>

        {proposal && (
          <div className="border border-dashed border-[var(--color-border-strong)] p-3 space-y-2">
            <span className="section-label flex items-center gap-1">
              <Sparkles size={12} /> Proponowane dane urzędu — zweryfikuj i dodaj
            </span>
            {(proposal.contact_url || proposal.website || proposal.source) && (
              <a
                href={proposal.contact_url || proposal.website || proposal.source}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
              >
                <ExternalLink size={11} /> Sprawdź adres i kontakt na stronie urzędu
              </a>
            )}
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="section-label">Nazwa urzędu *</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.name}
                  onChange={(e) => setProposalField("name", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Wydział (EGiB / geodezja)</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.department}
                  onChange={(e) => setProposalField("department", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Ulica *</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.street}
                  onChange={(e) => setProposalField("street", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Nr budynku *</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.building_no}
                  onChange={(e) => setProposalField("building_no", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Kod pocztowy *</span>
                <input className="input-base mt-0.5 text-xs font-mono" placeholder="00-000" value={proposal.postal_code}
                  onChange={(e) => setProposalField("postal_code", e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">Miejscowość *</span>
                <input className="input-base mt-0.5 text-xs" value={proposal.city}
                  onChange={(e) => setProposalField("city", e.target.value)} />
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setProposal(null)}>Odrzuć</Button>
              <Button
                size="sm"
                loading={addProposedOffice.isPending}
                disabled={!proposal.name || !proposal.street || !proposal.building_no || !proposal.postal_code || !proposal.city}
                onClick={() => addProposedOffice.mutate()}
              >
                Dodaj urząd i wybierz
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="section-label">Wnioskodawca</span>
            <div className="mt-1 flex gap-4">
              {([
                ["elektim", "ELEKTIM (my)"],
                ["energa", "ENERGA-OPERATOR"],
                ["tauron", "TAURON Dystrybucja"],
              ] as [WypisApplicant, string][]).map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={applicant === value} onChange={() => setApplicant(value)} />
                  {label}
                </label>
              ))}
            </div>
            {applicant !== "elektim" && (
              <div className="text-[11px] text-[var(--color-text-muted)] mt-1">
                Do sekcji 11 trafi prośba o wystawienie DOO na ELEKTIM.
              </div>
            )}
          </div>
          <label className="block">
            <span className="section-label">Skala</span>
            <select className="input-base mt-1" value={scale} onChange={(e) => setScale(e.target.value)}>
              <option value="1:500">1:500</option>
              <option value="1:1000">1:1000</option>
              <option value="1:2000">1:2000</option>
            </select>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="section-label">Cel pobrania (sekcja 8a)</span>
            <select
              className="input-base mt-1"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value as MzPurpose)}
            >
              {PURPOSE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="section-label">Sposób udostępnienia (sekcja 10)</span>
            <select
              className="input-base mt-1"
              value={delivery}
              onChange={(e) => setDelivery(e.target.value as MzDeliveryMethod)}
            >
              {DELIVERY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="section-label">Uwagi (dopisane do sekcji 11)</span>
          <textarea className="input-base mt-1 h-14" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <span className="block mt-1 text-[11px] text-[var(--color-text-muted)]">
            Treść „Proszę o wydanie mapy zasadniczej w postaci wektorowej, w formacie .dxf, w skali {scale},
            w układzie współrzędnych PL-2000{context ? ` (EPSG:${context.srid})` : ""}, obszar zgodnie z
            załącznikiem…” wpisze się automatycznie{multiCounty ? " (z informacją o granicy powiatu)" : ""}.
          </span>
        </label>

        {error && <div className="text-xs text-red-600">{error}</div>}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Anuluj</Button>
          <Button onClick={submit} loading={submitting} disabled={!canSubmit || submitting}>
            {selectedRows.length > 1
              ? `Utwórz ${selectedRows.length} ${selectedRows.length < 5 ? "zamówienia" : "zamówień"}`
              : "Utwórz zamówienie"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
